/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { Alert, INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import { IDictionary } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { IElicitCartlinesViewProps } from './components/elicit-cart-line-items';
import { IElicitCartViewProps } from './elicit-cart';

const _renderCartlines = (
  cartLines: IElicitCartlinesViewProps[] | undefined,
  cartEmptyText: string,
  CartlinesWrapper: INodeProps,
  storeSelector: React.ReactNode | undefined,
  backToShoppingButton: React.ReactNode,
  waitingComponent: React.ReactNode,
  cartLoadingStatus: string,
  cartDataResult: boolean,
  itemsTitle: string,
  qtyTitle: string,
  unitPriceTitle: string,
  totalTitle: string
): JSX.Element[] | JSX.Element => {
  if (cartLoadingStatus) {
    return <>{cartLoadingStatus}</>;
  }
  if (cartLines) {
    return (
      <table>
        <thead className='msc-cart-line__header'>
          <th scope='col' tabIndex={0} className='msc-cart-line__header-details'>{itemsTitle}</th>
          <th scope='col' tabIndex={0} className='msc-cart-line__header-quantity'>{qtyTitle}</th>
          <th scope='col' tabIndex={0} className='msc-cart-line__header-unitprice'>{unitPriceTitle}</th>
          <th scope='col' tabIndex={0} className='msc-cart-line__header-total'>{totalTitle}</th>
        </thead>
        <tbody>
          <div className='msc-cart-line__container'>
            {cartLines.map((cartLine, index) => {
              return (
                <div className='msc-cart-line__item-wrapper' key={index}>
                  {cartLine.cartline}
                  {storeSelector && cartLine.pickUpInStore ? (
                    <Node {...cartLine.pickUpInStore.ContainerProps}>
                      {cartLine.pickUpInStore.defaultComponent}
                    </Node>
                  ) : null}
                </div>
              );
            })}
          </div>
        </tbody>
      </table>
    );
  } else {
    return cartDataResult ? (
      <div className='msc-cart__empty-cart'>
        <p className='msc-cart__empty-cart-text'>{cartEmptyText}</p>
        {backToShoppingButton}
      </div>
    ) : (
      <>{waitingComponent}</>
    );
  }
};

const _renderOrderSummarylines = (
  orderSummaryLines: IOrderSummaryLines | undefined,
  OrderSummaryItems: INodeProps,
  props: IElicitCartViewProps
): JSX.Element | null => {
  if (!orderSummaryLines) {
    return null;
  }
  return (
    <Node {...OrderSummaryItems}>
      {orderSummaryLines.subtotal}
      {orderSummaryLines.shipping}
      {orderSummaryLines.tax}
      {orderSummaryLines.totalDiscounts
        ? orderSummaryLines.totalDiscounts
        : null}
      {orderSummaryLines.orderTotal}
      <p className='msc-order-summary__line-conditions'>{props.resources.elicitCart__conditionsText}</p>
    </Node>
  );
};

// Detect invalid items in the cart. Items are flagged externally via the Comment field
const _isInvalidCart = (cartLinesInvalid: IDictionary<boolean>): boolean => {

  for (const lineId of Object.keys(cartLinesInvalid)) {
    if (cartLinesInvalid[lineId]) {
      return true;
    }
  }

  return false;
};

//==================================================
//==================================================
const ElicitCartView: React.FC<IElicitCartViewProps> = (props: IElicitCartViewProps) => {
  const preventCheckout = _isInvalidCart(props.cartLineStatus) || props.mixedCart.isMixedCart;
  const { faqLink } = props.config;

  return (
    <div
      className={classnames(props.className, { 'msc-cart__empty': !props.cartlines })}
      id={props.id}
      {...props.renderModuleAttributes(props)}
    >
      {props.cartlines && props.title}
      {props.subscriptionUpsell}
      {props.subscriptionHeader}
      <Node {...props.CartlinesWrapper}>
        <Alert isOpen={props.cartlines !== undefined && props.mixedCart && props.mixedCart.isMixedCart} className='msc-cart__error' color='danger' assertive={true} >
          <div className='msc-cart__error-content' aria-hidden='true'>
            <span>{props.resources.elicitCart__mixModeCartError}</span>
          </div>
        </Alert>
        {_renderCartlines(
          props.cartlines,
          props.resources.elicitCart__emptyCartText,
          props.CartlinesWrapper,
          props.storeSelector,
          props.backToShoppingButton,
          props.waitingComponent,
          props.cartLoadingStatus,
          props.cartDataResult,
          props.resources.elicitCart__itemsTitle,
          props.resources.elicitCart__qtyTitle,
          props.resources.elicitCart__unitPriceTitle,
          props.resources.elicitCart__totalTitle
        )}
      </Node>
      {props.shouldShowCheckoutInline && (preventCheckout ? props.checkoutButtonDisabled : props.checkoutAsSignInUserButton)}
      {props.orderSummaryHeading && (
        <Node {...props.OrderSummaryWrapper}>
          <div className='msc-order-summary__header'>
            {props.orderSummaryHeading}
            {
              faqLink?.linkUrl?.destinationUrl &&
              <a
                className='msc-order-summary__faq'
                href={faqLink.linkUrl.destinationUrl}
                target={faqLink.openInNewTab ? '_blank' : undefined}
                aria-label={faqLink.ariaLabel}
                role='button' rel="noreferrer"
              />
            }
          </div>
          {_renderOrderSummarylines(
            props.orderSummaryLineitems,
            props.OrderSummaryItems,
            props
          )}
          <div className='msc-promo-code'>
            {props.promoCode}
          </div>
          <Node className='msc-cart__checkout'>
            {preventCheckout ? props.checkoutButtonDisabled : props.checkoutAsSignInUserButton}
            {props.backToShoppingButton}
          </Node>
        </Node>
      )}
    </div>
  );
};

export default ElicitCartView;